import axios from 'axios';
import { axiosInstance } from './api';
import { API_BASE } from './base';

// Гостевой портал Wi-Fi (W3/W4).
export type CaptiveSlot = 'title' | 'subtitle' | 'phone_prompt' | 'legal' | 'code_prompt'
  | 'success_title' | 'success_text' | 'button';
export type TextSize = 'sm' | 'md' | 'lg' | 'xl';
export type TextWeight = 'thin' | 'normal' | 'bold' | 'caps';
export type CaptiveTexts = Partial<Record<CaptiveSlot, { text: string; size: TextSize; weight: TextWeight }>>;

export interface CaptiveOfferDto { id: number; caption: string; order: number; url: string }

export interface CaptivePortalDto {
  public_id: string;
  is_active: boolean;
  identity: 'linza' | 'surf';
  texts: CaptiveTexts;
  offers: CaptiveOfferDto[];
  updated_at: string;
  router: CaptiveRouterDto;
}

export interface CaptiveRouterDto {
  can_push: boolean;
  blockers: string[];
  uam_server: string;
  radius_host: string;
  nas_id: string;
  pushed_at: string | null;
  push_error: string;
}

export interface CaptiveSettingsDto {
  portal: CaptivePortalDto | null;
  branch_name: string;
  address: string;
}

export interface PublicCaptiveDto {
  branch_name: string;
  address: string;
  identity: 'linza' | 'surf';
  texts: CaptiveTexts;
  offers: CaptiveOfferDto[];
}

export const getCaptiveSettings = (zoneId: string | number) =>
  axiosInstance.get<CaptiveSettingsDto>(`/equipment_discovery/zones/${zoneId}/captive/`);

export const putCaptivePortal = (
  zoneId: string | number,
  payload: Partial<Pick<CaptivePortalDto, 'identity' | 'is_active' | 'texts'>>,
) => axiosInstance.put<{ portal: CaptivePortalDto }>(`/equipment_discovery/zones/${zoneId}/captive/`, payload);

export const uploadCaptiveOffer = (zoneId: string | number, file: File, caption = '') => {
  const body = new FormData();
  body.append('file', file);
  body.append('caption', caption);
  return axiosInstance.post<{ portal: CaptivePortalDto }>(`/equipment_discovery/zones/${zoneId}/captive/offers/`, body);
};

export const deleteCaptiveOffer = (zoneId: string | number, id: number) =>
  axiosInstance.delete<{ portal: CaptivePortalDto }>(`/equipment_discovery/zones/${zoneId}/captive/offers/`, { data: { id } });

const publicHttp = axios.create({ baseURL: API_BASE, timeout: 20000 });

export const getPublicCaptive = (publicId: string) =>
  publicHttp.get<PublicCaptiveDto>(`/equipment_discovery/captive/${publicId}/`);

/** Параметры перехвата, которые роутер (chilli) дописывает к адресу портала. */
export type ChilliParams = Record<string, string>;

export const readChilliParams = (search: string): ChilliParams => {
  const keys = ['uamip', 'uamport', 'challenge', 'mac', 'nasid', 'userurl', 'called', 'sessionid'];
  const q = new URLSearchParams(search);
  const out: ChilliParams = {};
  keys.forEach((k) => { const v = q.get(k); if (v) out[k] = v; });
  return out.uamip && out.challenge ? out : {};
};

export const requestCaptiveCode = (publicId: string, phone: string, uam: ChilliParams = {}) =>
  publicHttp.post<{ ok: boolean }>(`/equipment_discovery/captive/${publicId}/request-code/`, { phone, uam });

export const verifyCaptiveCode = (publicId: string, phone: string, code: string, uam: ChilliParams = {}) =>
  publicHttp.post<{ ok: boolean; redirect_url: string }>(
    `/equipment_discovery/captive/${publicId}/verify/`, { phone, code, uam });

export const pushCaptiveToRouter = (zoneId: string | number) =>
  axiosInstance.post<{ ok: boolean; portal: CaptivePortalDto }>(
    `/equipment_discovery/zones/${zoneId}/captive/push/`, {});

/** Ссылка на гостевой портал — от текущего origin, чтобы стенд не подсовывал
 *  ссылку другого стенда (та же правка, что для формы отзывов). */
export const captiveUrl = (publicId: string) =>
  `${typeof window === 'undefined' ? 'https://my.linzametrics.com' : window.location.origin}/wifi/${publicId}`;
export const assetUrl = (path: string) => `${API_BASE}${path}`;
